/*
 *  @endpoint: /callDashboard/:phoneNumber (authenticated) or /callDashboard/:encryptedToken (external)
 * @requires: userType = 'sales
*/
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { userType } from "../utils/dictionaries"
import SalesDashboard from "../components/callDashboard/SalesDashboard";
import { findCustomersByMobileEmailStudentNumber, decryptCustomerSearchString, authenticateWithToken } from "../services/SoftphoneService"
import { setUserSession, getToken } from "../utils/Auth"
import axios from "axios";

const CallDashboard = () => {
    const [callDetails, setCallDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [initialMatches, setInitialMatches] = useState([]);
    const { param } = useParams();
    const history = useHistory();
    
    const isShortToken = param && /^[a-f0-9]{16}$/i.test(param);
    const isTokenizedUrl = isShortToken || (param && param.includes(':'));
    const phoneNumber = !isTokenizedUrl && param ? param.replace(/\+/g, '') : null;
    const encryptedToken = isTokenizedUrl ? param : null;
    
    useEffect(() => {
        const source = axios.CancelToken.source();
        
        if (isTokenizedUrl && encryptedToken) {
            // Handle tokenized URL: authenticate and redirect to phone number URL
            const authenticateAndRedirect = async () => {
                try {
                    setLoading(true);
                    
                    // Check if already authenticated
                    const existingToken = getToken();
                    if (existingToken) {
                        // Already logged in, just get phone number and redirect
                        const decryptRes = await decryptCustomerSearchString(
                            { token: encryptedToken }, 
                            source.token
                        );
                        
                        if (decryptRes?.status === 200 && decryptRes?.data?.customerData) {
                            let phoneNumber = typeof decryptRes.data.customerData === 'string' 
                                ? decryptRes.data.customerData 
                                : decryptRes.data.customerData.mobileNumber;
                            
                            phoneNumber = phoneNumber ? phoneNumber.replace(/\+/g, '') : phoneNumber;
                            
                            history.replace(`/callDashboard/${phoneNumber}`);
                            return;
                        }
                    }
                    
                    // Not authenticated - authenticate using token
                    const authRes = await authenticateWithToken(
                        { token: encryptedToken }, 
                        source.token
                    );
                    
                    if (authRes?.status === 200 && authRes?.data) {
                        // Store authentication token and user
                        setUserSession(authRes.data.token, authRes.data.user);
                        
                        const phoneNum = authRes.data.phoneNumber ? authRes.data.phoneNumber.replace(/\+/g, '') : authRes.data.phoneNumber;
                        history.replace(`/callDashboard/${phoneNum}`);
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        console.error("Error authenticating with token:", error);
                        if (error.response?.status === 401) {
                            setCallDetails({
                                error: error.response?.data?.message || 'Invalid or expired token. Please request a new tokenized URL.'
                            });
                        } else {
                            setCallDetails({
                                error: 'Failed to authenticate. Please try again.'
                            });
                        }
                        setLoading(false);
                    }
                }
            }
            authenticateAndRedirect();
        } else if (phoneNumber) {
            const fetchCallDetails = async () => {
                try {
                    setLoading(true);
                    // Search by phone numbe
                    const res = await findCustomersByMobileEmailStudentNumber(
                        { mobileNumber: phoneNumber }, 
                        source.token
                    );
                    
                    if (res?.status === 200 && res?.data) {
                        const customers = Array.isArray(res.data) ? res.data : [];
                        
                        if (customers.length === 1) {
                            // Single match
                            const customer = customers[0];
                            const mappedCustomer = {
                                ...customer,
                                studentNumber: customer.customerId || customer.studentNumber,
                                mobile: phoneNumber, // Use phone number from URL
                                deliverymethod: customer.learningMethod || customer.deliverymethod,
                                status: customer.stage || customer.status,
                                userType: 'sales'
                            };
                            setCallDetails(mappedCustomer);
                            setInitialMatches([]);
                        } else if (customers.length > 1) {
                            // Multiple matches - store for CustomerBlock to handle
                            setInitialMatches(customers);
                            setCallDetails({});
                        } else {
                            // No matches
                            setCallDetails({});
                            setInitialMatches([]);
                        }
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        console.error("Error fetching customer:", error);
                    }
                } finally {
                    setLoading(false);
                }
            }
            fetchCallDetails();
        }
        
        return () => {
            source.cancel();
        }
    }, [phoneNumber, encryptedToken, isTokenizedUrl]);
    return (
        <div className="call-dashboard-page">
            {/* Scrollable content area */}
            <div className="call-dashboard-content">
                {/* Page title - scrolls with content */}
                <div className="pt-3 dashboard-hide-small">
                <h4 className=" page-title-wrap text-left-align landing-heading heading_color_dynamic" >
                                Sales <span>Call Dashboard</span>
                            </h4>
                </div>

                {loading ? (
                    <div className="p-4 text-center">Loading...</div>
                ) : callDetails.error ? (
                    <div className="p-4 text-center">
                        <div className="alert alert-danger" role="alert">
                            <h5 className="alert-heading">Error</h5>
                            <p>{callDetails.error}</p>
                        </div>
                    </div>
                ) : (
                    <SalesDashboard 
                        callDetails={callDetails} 
                        encryptedString={isTokenizedUrl ? encryptedToken : null}
                        phoneNumber={phoneNumber}
                        initialMatches={initialMatches}
                    />
                )}
            </div>
        </div>
    );
};

export default CallDashboard;
